import React, { Component, createRef, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, useNavigate } from "react-router-dom";
import { Row, Col, Card, Table, Typography, DatePicker, Button, Space, Empty, Spin, Input, Popconfirm, Tooltip } from "antd";
import { LeftCircleFilled, RightCircleFilled } from "@ant-design/icons";
import { faEdit, faFile, faFileInvoice, faPlus, faTrash, faUserCircle } from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import dayjs from "dayjs";
import _ from "lodash";
import ModalAddTimeEntry from "./ModalAddTimeEntry";
import ModalEditTimeEntry from "./ModalEditTimeEntry";

function TimeSheet(props) {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [startDate, setStartDate] = useState(dayjs().startOf("day"));
  const [endDate, setEndDate] = useState(dayjs().endOf("day"));

  const [timeEntriesSearchTerm, setTimeEntriesSearchTerm] = useState("");
  const [timeEntries, setTimeEntries] = useState([]);
  const [filteredTimeEntries, setFilteredTimeEntries] = useState([]);

  const [addTimeEntryProps, setAddTimeEntryProps] = useState({
    open: false,
    job_id: null,
    task_id: null,
    client_id: null,
    refresh: false,
  });

  const [editTimeEntryProps, setEditTimeEntryProps] = useState({
    open: false,
    job_id: null,
    task_id: null,
    time_id: null,
    client_id: null,
    refresh: false,
  });

  const GetUsers = async () => {
    let response = await props.AxiosRequest("/api/users/get");
    let users = response.data.users.map((m) => {
      return { ...m, key: m.id, label: m.name };
    });
    setUsers(users);
    let selected_user = users.find((f) => f.current_user === 1);
    if (selected_user) setSelectedUser(selected_user);
  };
  const GetTimeEntries = async () => {
    let response = await props.AxiosRequest("/api/time-entries/get", { time_by: selectedUser.id, start_date: startDate, end_date: endDate });
    setTimeEntries(response.data.time_entries);
  };
  const DeleteTimeEntry = async (id) => {
    await props.AxiosRequest("/api/time-entries/delete", { id });
    GetTimeEntries();
  };
  const AddInvoiceAndGoToInvoice = async (client_id) => {
    let request = await props.AxiosRequest("/api/invoices/insert", { clients_xref: client_id });
    navigate("/invoicing/" + request.data.tag);
  };
  const FilterTimeEntries = async () => {
    if (!timeEntriesSearchTerm) return setFilteredTimeEntries(timeEntries);
    let filtered_list = timeEntries.filter((f) =>
      _.toLower(f.comments).indexOf(_.toLower(timeEntriesSearchTerm)) > -1 ||
      _.toLower(f.task_statuses_name).indexOf(_.toLower(timeEntriesSearchTerm)) > -1 ||
      _.toLower(f.clients_name).indexOf(_.toLower(timeEntriesSearchTerm)) > -1 ||
      _.toLower(f.items_name).indexOf(_.toLower(timeEntriesSearchTerm)) > -1 ||
      _.toLower(f.due_at).indexOf(_.toLower(timeEntriesSearchTerm)) > -1 ||
      _.toLower(f.time_by).indexOf(_.toLower(timeEntriesSearchTerm)) > -1 ||
      _.toLower(f.time_at).indexOf(_.toLower(timeEntriesSearchTerm)) > -1 ||
      _.toLower(f.jobs_name).indexOf(_.toLower(timeEntriesSearchTerm)) > -1 ||
      _.toLower(f.tasks_name).indexOf(_.toLower(timeEntriesSearchTerm)) > -1 ||
      _.toLower(f.hours).indexOf(_.toLower(timeEntriesSearchTerm)) > -1 ||
      _.toLower(f.bill_rate).indexOf(_.toLower(timeEntriesSearchTerm)) > -1 ||
      _.toLower(f.billing_statuses_name).indexOf(_.toLower(timeEntriesSearchTerm)) > -1 ||
      _.toLower(props.MakeDateTimeReadable(f.promised_by)).indexOf(_.toLower(timeEntriesSearchTerm)) > -1
        ? true
        : false,
    );
    setFilteredTimeEntries(filtered_list);
  };

  useEffect(() => {
    GetUsers();
  }, []);

  useEffect(() => {
    FilterTimeEntries();
  }, [timeEntriesSearchTerm, timeEntries]);

  useEffect(() => {
    if (selectedUser && startDate && endDate) GetTimeEntries();
  }, [selectedUser, startDate, endDate]);

  useEffect(() => {
    if (addTimeEntryProps?.refresh || editTimeEntryProps?.refresh) {
      setAddTimeEntryProps({ ...addTimeEntryProps, refresh: false });
      setEditTimeEntryProps({ ...editTimeEntryProps, refresh: false });
      GetTimeEntries();
    }
  }, [addTimeEntryProps?.refresh, editTimeEntryProps?.refresh]);

  const time_sheet_columns = [
    {
      title: "Date",
      dataIndex: "time_at",
      key: "time_at",
      render: (value) => props.MakeDateTimeReadable(value),
      sorter: (a, b) => (a.time_at > b.time_at ? 1 : -1),
    },
    {
      title: "Client",
      dataIndex: "clients_name",
      key: "clients_name",
      render: (value, object) => <Link to={"/client-details/" + object.clients_tag}>{value}</Link>,
      sorter: (a, b) => (a.clients_name > b.clients_name ? 1 : -1),
    },
    {
      title: "Job",
      dataIndex: "jobs_name",
      key: "jobs_name",
      sorter: (a, b) => (a.jobs_name > b.jobs_name ? 1 : -1),
    },
    {
      title: "Task",
      dataIndex: "tasks_name",
      key: "tasks_name",
      sorter: (a, b) => (a.tasks_name > b.tasks_name ? 1 : -1),
    },
    {
      title: "Item",
      dataIndex: "items_name",
      key: "items_name",
      sorter: (a, b) => (a.items_name > b.items_name ? 1 : -1),
    },
    {
      title: "Task Status",
      dataIndex: "task_statuses_name",
      key: "task_statuses_name",
      sorter: (a, b) => (a.task_statuses_name > b.task_statuses_name ? 1 : -1),
    },
    {
      title: "Comments",
      dataIndex: "comments",
      key: "comments",
      sorter: (a, b) => (a.comments > b.comments ? 1 : -1),
      render: (value) => <Input.TextArea value={value} style={{ resize: "both" }} />,
    },
    {
      title: "Hours",
      align: "right",
      dataIndex: "hours",
      key: "hours",
      render: (value) => props.FormatNumberTwoDecimals(value),
      sorter: (a, b) => (a.hours > b.hours ? 1 : -1),
    },
    {
      title: "Bill Rate",
      align: "right",
      dataIndex: "bill_rate",
      key: "bill_rate",
      render: (value) => props.FormatNumberToCurrency(value),
      sorter: (a, b) => (a.bill_rate > b.bill_rate ? 1 : -1),
    },
    {
      title: "Extended Rate",
      align: "right",
      dataIndex: "bill_rate",
      key: "bill_rate",
      render: (value, object) => props.FormatNumberToCurrency(value * object.hours),
      sorter: (a, b) => (a.value * a.hours > b.value * b.hours ? 1 : -1),
    },
    {
      title: "Billing Status",
      dataIndex: "billing_statuses_name",
      key: "billing_statuses_name",
      sorter: (a, b) => (a.billing_statuses_name > b.billing_statuses_name ? 1 : -1),
    },
    {
      title: "Actions",
      dataIndex: "id",
      key: "id",
      align: "right",
      width: "250px",
      render: (value, object) => (
        <Row gutter={[8, 8]}>
          <Col span={24}>
            <Button
              size="small"
              type="primary"
              style={{ minWidth: "115px" }}
              onClick={() => {
                setEditTimeEntryProps({ ...editTimeEntryProps, time_id: object.id, client_id: object.clients_xref, job_id: object.jobs_xref, task_id: object.tasks_xref, open: true, editing: true });
              }}
            >
              <FontAwesomeIcon icon={faEdit} className="mr-1" />
              Edit Time
            </Button>
          </Col>
          {object.invoices_xref ? (
            <Col span={24}>
              <Link to={"/invoicing/" + object.invoices_tag}>
                <Button size="small" style={{ minWidth: "115px" }}>
                  <FontAwesomeIcon icon={faFileInvoice} className="mr-1" />
                  View Invoice
                </Button>
              </Link>
            </Col>
          ) : (
            <Col span={24}>
              <Popconfirm
                title="Would you like to create an invoice for this client?"
                description="Would you like to create an invoice for this client and go to that invoice?"
                okText="Create Invoice"
                cancelText="Cancel"
                onConfirm={() => {
                  AddInvoiceAndGoToInvoice(object.clients_xref);
                }}
              >
                <Button size="small" style={{ minWidth: "115px" }}>
                  <FontAwesomeIcon icon={faFileInvoice} className="mr-1" />
                  Add Invoice
                </Button>
              </Popconfirm>
            </Col>
          )}
          <Col span={24}>
            <Popconfirm
              title="Are you sure?"
              description="Are you sure you want to delete this time entry?"
              onConfirm={() => {
                DeleteTimeEntry(object.id);
              }}
            >
              <Button size="small" danger style={{ minWidth: "115px" }}>
                <FontAwesomeIcon icon={faTrash} className="mr-1" />
                Delete Time
              </Button>
            </Popconfirm>
          </Col>
        </Row>
      ),
    },
  ];

  return (
    <>
      <main className="content">
        <div className="container-fluid">
          <div className="header">
            <h1 className="header-title">Time Sheet</h1>
          </div>
          {/* Time sheet card */}
          <Card title={<Typography.Title level={5}>Time Sheet</Typography.Title>}>
            <Row gutter={[16, 16]}>
              {/* User selector */}
              {props.securitySettings.find((f) => f.setting === "time_sheet_restrict")?.viewable === 1 ? (
                <Col span={4}>
                  <label className="col-form-label-sm">User</label>
                  <Select
                    inputId="user-select"
                    options={props.MakeUserOptions(users)}
                    value={selectedUser}
                    onChange={(e) => {
                      setSelectedUser(e);
                    }}
                  />
                </Col>
              ) : null}
              {/* Date range selector */}
              <Col span={4}>
                <label className="col-form-label-sm">Date Range</label>
                <DatePicker.RangePicker
                  className="w-100"
                  allowClear={false}
                  value={[startDate, endDate]}
                  onChange={(e) => {
                    setStartDate(e[0]);
                    setEndDate(e[1]);
                  }}
                  format="MM/DD/YYYY"
                />
              </Col>

              <Col span={1} className="text-center">
                <label className="col-form-label-sm">&nbsp;</label>
                <Tooltip title="Move the Date Range Backward by 1 Day">
                  <Button
                    className="w-100"
                    type="ghost"
                    icon={<LeftCircleFilled size="large" />}
                    onClick={() => {
                      setStartDate(dayjs(startDate).subtract(1, "day").startOf("day"));
                      setEndDate(dayjs(endDate).subtract(1, "day").endOf("day"));
                    }}
                  />
                </Tooltip>
              </Col>
              <Col span={1} className="text-center">
                <label className="col-form-label-sm">&nbsp;</label>
                <Tooltip title="Move the Date Range Forward by 1 Day">
                  <Button
                    className="w-100"
                    type="ghost"
                    icon={<RightCircleFilled size="large" />}
                    onClick={() => {
                      setStartDate(dayjs(startDate).add(1, "day").startOf("day"));
                      setEndDate(dayjs(endDate).add(1, "day").endOf("day"));
                    }}
                  />
                </Tooltip>
              </Col>

              <Col span={props.securitySettings.find((f) => f.setting === "time_sheet_restrict")?.viewable === 1 ? 14 : 18} className="text-right">
                <label className="col-form-label-sm">&nbsp;</label>
                <br />
                <Space>
                  <Button
                    type="primary"
                    onClick={() => {
                      setAddTimeEntryProps({ ...addTimeEntryProps, open: true, default_date: startDate });
                    }}
                  >
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                    Add A New Time Entry
                  </Button>
                  <Input
                    value={timeEntriesSearchTerm}
                    onChange={(e) => {
                      setTimeEntriesSearchTerm(e.target.value);
                    }}
                    placeholder="Search..."
                    allowClear
                  />
                </Space>
              </Col>
              <Col span={24}>
                <Table
                  // pagination={false}
                  locale={{ emptyText: filteredTimeEntries ? <Empty /> : <Spin /> }}
                  columns={time_sheet_columns}
                  dataSource={filteredTimeEntries}
                  summary={(pageData) => {
                    let total_hours = 0,
                      total_extended_rate = 0;
                    filteredTimeEntries.forEach(({ hours, bill_rate }) => {
                      total_hours += hours;
                      total_extended_rate += hours * bill_rate;
                    });
                    return (
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0}>Summary</Table.Summary.Cell>
                        <Table.Summary.Cell index={1} />
                        <Table.Summary.Cell index={2} />
                        <Table.Summary.Cell index={3} />
                        <Table.Summary.Cell index={4} />
                        <Table.Summary.Cell index={5} />
                        <Table.Summary.Cell index={6} />
                        <Table.Summary.Cell index={7} align="right">
                          {props.FormatNumberTwoDecimals(total_hours)}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={8} />
                        <Table.Summary.Cell index={9} align="right">
                          {props.FormatNumberToCurrency(total_extended_rate)}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={10} />
                        <Table.Summary.Cell index={11} />
                      </Table.Summary.Row>
                    );
                  }}
                />
              </Col>
            </Row>
            <br></br>
            <ModalAddTimeEntry {...props} addTimeEntryProps={addTimeEntryProps} setAddTimeEntryProps={setAddTimeEntryProps} />
            <ModalEditTimeEntry {...props} editTimeEntryProps={editTimeEntryProps} setEditTimeEntryProps={setEditTimeEntryProps} />
            {/* table */}
          </Card>
        </div>
      </main>
    </>
  );
}

export default TimeSheet;
